/*
 * @Descripttion: 本地软件服务通用调用工具（业务默认 127.0.0.1:37777，健康检查默认 127.0.0.1:3777/version）
 * @version:
 * @Author: ZQian
 * @email:
 * @Date: 2026-09-15
 * @LastEditors: ZQian
 * @LastEditTime: 2026-09-21
 */

/**
 * 本地软件服务通用调用工具
 *
 * 特性：
 * 1. 全程异步，支持两种用法：await 返回值，或 onSuccess/onError/onComplete 回调，两种方式可并存；
 * 2. 只需"把调用发出去、不关心返回"的场景：直接调用、不接返回值即可，
 *    内部保证绝不抛错、不会产生未处理的 Promise 拒绝；
 * 3. 调用前先请求健康检查地址（默认 http://127.0.0.1:3777/version）探测本地软件是否可通信，
 *    无正常返回（请求失败/超时/非 2xx）则直接放弃，不发起业务请求；
 * 4. 任何失败（健康检查不过、软件未启动、接口异常、超时、响应解析失败）都不会抛错，
 *    也不会触发全局错误提示，统一返回 { ok: false, reason, detail }，不影响主业务；
 * 5. 健康检查成功结果短时间缓存，适合高频/轮询场景，避免每次都先探测一次。
 */

/** 本地服务默认地址（端口可通过本地软件 exe 同级目录 config.json 的 Port 字段修改） */
export const LOCAL_SERVICE_HOST = 'http://127.0.0.1:37777'

/** 当前使用的本地服务地址，初始取默认值，可通过 setLocalServiceHost 全局修改 */
let currentHost = LOCAL_SERVICE_HOST

/**
 * 全局修改本地服务地址（端口改动时只需在应用入口调用一次）。
 * 例如本地软件 config.json 把 Port 改成 9001：setLocalServiceHost('http://127.0.0.1:9001')
 */
export function setLocalServiceHost(host: string): void {
  currentHost = host
  clearLocalServiceAliveCache()
}

/** 获取当前使用的本地服务地址 */
export function getLocalServiceHost(): string {
  return currentHost
}

/** 本地服务健康检查地址：请求该接口有正常返回（HTTP 2xx）即认为本地软件开启且可通信 */
export const LOCAL_SERVICE_HEALTH_URL = 'http://127.0.0.1:37777/version'

/** 当前使用的健康检查地址，初始取默认值，可通过 setLocalServiceHealthUrl 全局修改 */
let currentHealthUrl = LOCAL_SERVICE_HEALTH_URL

/** 全局修改本地服务健康检查地址（本地软件健康检查端口/路径变动时在应用入口调用一次） */
export function setLocalServiceHealthUrl(url: string): void {
  currentHealthUrl = url
  clearLocalServiceAliveCache()
}

/** 获取当前使用的健康检查地址 */
export function getLocalServiceHealthUrl(): string {
  return currentHealthUrl
}

/** 探测/健康检查超时时间（ms），探测要快，失败尽快放弃 */
const DEFAULT_PROBE_TIMEOUT = 3000

/** 业务请求超时时间（ms） */
const DEFAULT_CALL_TIMEOUT = 10000

/** 端口探测成功结果缓存时长（ms） */
const PROBE_CACHE_TTL = 3000

/** 本地服务调用失败原因 */
export type LocalServiceFailReason =
  /** 本地软件未启动 / 端口不通 / 网络不可达 */
  | 'unreachable'
  /** 请求超时 */
  | 'timeout'
  /** 接口返回了非 2xx 状态码 */
  | 'httpError'
  /** 其他未知错误 */
  | 'unknown'
  /** 后端获取报文接口失败（sendCompliancePayload 场景） */
  | 'backendError'
  /** 入参缺失（如 treatmentId 未传） */
  | 'invalidParams'

export interface LocalServiceResult<T = any> {
  /** 本次调用是否成功 */
  ok: boolean
  /** 响应内容：JSON 格式时为解析后的对象，否则为原始文本（仅 ok 时有值） */
  data?: T
  /** 失败原因，仅用于内部排查，不会展示给用户 */
  reason?: LocalServiceFailReason
  /** 失败细节，仅用于内部排查，不会展示给用户 */
  detail?: string
}

export interface CallLocalServiceOptions<T = any> {
  /** 接口路径，如 '/api/xxx'，默认空（请求 host 根路径） */
  path?: string
  /** 请求方法，默认 POST（按服务文档约定：分析类接口统一为 POST，版本查询接口用 GET） */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  /** URL 查询参数，值为 undefined/null 的项会被忽略 */
  params?: Record<string, string | number | boolean | undefined | null>
  /** 请求体，POST/PUT 时以 application/json;charset=UTF-8 发送（标准 JSON，入参 camelCase/PascalCase 均可） */
  body?: any
  /** 同步/异步调用模式请求头 isNeedRes（按服务文档约定传值，如 true/false），不传则使用服务默认模式 */
  isNeedRes?: boolean | string
  /** 额外请求头 */
  headers?: Record<string, string>
  /** 本地服务业务地址，默认取全局配置（可用 setLocalServiceHost 修改）；注意：调用前的健康检查地址与之无关，由 setLocalServiceHealthUrl 配置 */
  host?: string
  /** 业务请求超时时间 ms，默认 10000 */
  timeout?: number
  /** 跳过健康检查直接调用，默认 false（先探测本地软件可通信，无正常返回则不发起业务请求） */
  skipProbe?: boolean
  /** 成功回调：接口正常返回时触发，data 为解析后的 JSON（非 JSON 响应为原始文本）；后续对返回数据的处理写在这里 */
  onSuccess?: (data?: T) => void
  /** 失败回调（可选），用于静默上报/排查日志，方法本身绝不抛错、绝不弹错 */
  onError?: (reason: LocalServiceFailReason, detail?: string) => void
  /** 完成回调：无论成功失败最后都会触发，用 result.ok 区分成败（适合收尾动作，如关闭 loading） */
  onComplete?: (result: LocalServiceResult<T>) => void
}

/** 健康检查成功结果缓存，按 url 分开 */
const aliveCacheMap: Record<string, { expiredAt: number }> = {}

/** 清空端口探测缓存（例如本地软件重启后希望立即重新探测时调用） */
export function clearLocalServiceAliveCache(): void {
  Object.keys(aliveCacheMap).forEach((key) => delete aliveCacheMap[key])
}

/**
 * 判断指定地址+端口是否可以通信（独立通用方法，与本地服务业务调用解耦）。
 * 原理：向 http://{address}:{port}/ 发一个 no-cors 的 GET 请求，
 * 只要端口有任何响应（即使是 404/400）就视为可通信；
 * 端口不通、软件未启动、超时都返回 false，永远不会抛错。
 * 每次调用都真实探测、不读写缓存，适合"检测连接"按钮、业务调用前手动把关等场景。
 * 注意：判断本地合规分析软件是否可通信请用 isLocalServiceAlive（走 /version 健康检查）。
 *
 * @param address IP 或域名，如 '127.0.0.1'（兼容带 http:// 前缀或已带端口的写法）
 * @param port 端口号，如 37777（address 中已带端口时忽略此参数）
 * @param timeout 超时时间 ms，默认 3000
 *
 * @example
 * if (await isPortAlive('127.0.0.1', 8090)) {
 *   // 端口可通信，再执行后续业务
 * }
 */
export async function isPortAlive(
  address: string,
  port: number,
  timeout: number = DEFAULT_PROBE_TIMEOUT
): Promise<boolean> {
  const clean = address.replace(/^https?:\/\//, '').replace(/\/+$/, '')
  const url = clean.includes(':') ? `http://${clean}/` : `http://${clean}:${port}/`
  return probeUrl(url, timeout)
}

/**
 * 探测当前配置的本地服务是否在线（callLocalService / sendCompliancePayload 的前置探测即此方法）。
 * 探测方式：GET 健康检查地址（默认 http://127.0.0.1:3777/version），
 * 有正常返回（HTTP 2xx）即认为本地软件开启且可通信；请求失败、超时、非 2xx 都视为不可通信。
 * 成功结果会写入 3 秒缓存供高频调用复用；失败不缓存，下次调用会重新探测。
 * 只返回 true/false，永远不会抛错。
 */
export async function isLocalServiceAlive(
  options: { healthUrl?: string; timeout?: number } = {}
): Promise<boolean> {
  const healthUrl = options.healthUrl || currentHealthUrl
  const cached = aliveCacheMap[healthUrl]
  if (cached && cached.expiredAt > Date.now()) {
    return true
  }
  const alive = await checkUrlOk(healthUrl, options.timeout || DEFAULT_PROBE_TIMEOUT)
  if (alive) {
    aliveCacheMap[healthUrl] = { expiredAt: Date.now() + PROBE_CACHE_TTL }
  }
  console.log(`[LocalService] ${healthUrl} ${alive ? 'alive' : 'dead'}`)
  return alive
}

/**
 * 底层端口探测：no-cors GET 一次，请求有响应即认为端口可通信，任何异常都返回 false。
 */
async function probeUrl(url: string, timeout: number): Promise<boolean> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)
  try {
    await fetch(url, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    })
    return true
  } catch (e) {
    // 端口不通/超时都视为不可通信，且不抛错
    return false
  } finally {
    clearTimeout(timer)
  }
}

/**
 * 底层健康检查：GET url 且要求正常返回（HTTP 2xx）才认为可通信，任何异常都返回 false。
 * 需要本地软件对该接口放行跨域（CORS），否则浏览器拦截响应也会判定为不可通信。
 */
async function checkUrlOk(url: string, timeout: number): Promise<boolean> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)
  try {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      signal: controller.signal,
    })
    return response.ok
  } catch (e) {
    // 请求失败/超时/跨域被拦截都视为不可通信，且不抛错
    return false
  } finally {
    clearTimeout(timer)
  }
}

/**
 * 调用本地软件服务（通用方法）。
 *
 * 流程：先做健康检查（默认 GET http://127.0.0.1:3777/version）→ 无正常返回则直接放弃，
 * 不发起业务请求 → 通过则发起请求并解析响应。
 * 任何异常都被吞掉并转为 { ok: false }，不影响调用方主业务，也不会弹出全局错误。
 *
 * @example
 * // 版本查询接口（GET），await 返回值的用法
 * const res = await callLocalService<{ version: string }>({ path: '/api/version', method: 'GET' })
 * if (res.ok) { console.log(res.data) }
 *
 * @example
 * // 现有业务：只负责把调用发出去、不关心返回 —— 直接调用、不接返回值即可
 * // （内部保证绝不抛错、不会产生未处理的 Promise 拒绝，完全不影响主业务）
 * callLocalService({ path: '/api/analyze', body: { patientId: '123' }, isNeedRes: true })
 *
 * @example
 * // 后续需要对返回数据做处理时：设置 onSuccess 回调（与 await 返回值可并存）
 * callLocalService({
 *   path: '/api/analyze',
 *   body: { patientId: '123' },
 *   onSuccess: (data) => handleAnalyzeResult(data),
 *   onError: (reason, detail) => console.warn('本地服务调用失败', reason, detail),
 *   onComplete: (result) => console.log('调用结束，是否成功：', result.ok),
 * })
 */
export async function callLocalService<T = any>(
  options: CallLocalServiceOptions<T>
): Promise<LocalServiceResult<T>> {
  const host = options.host || currentHost
  const method = options.method || 'POST'
  // 统一分发回调：成功走 onSuccess、失败走 onError、最后必走 onComplete；
  // 回调自身抛出的异常也会被吞掉，保证整体绝不报错
  const finish = (result: LocalServiceResult<T>): LocalServiceResult<T> => {
    if (result.ok) {
      safeInvoke(options.onSuccess, result.data)
    } else {
      safeInvoke(options.onError, result.reason, result.detail)
    }
    safeInvoke(options.onComplete, result)
    return result
  }
  const fail = (reason: LocalServiceFailReason, detail?: string): LocalServiceResult<T> =>
    finish({ ok: false, reason, detail })

  try {

    // 1. 发起业务请求（独立超时控制）
    const url = `${host}${options.path || ''}${buildQuery(options.params)}`
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), options.timeout || DEFAULT_CALL_TIMEOUT)
    let response: Response
    try {
      const hasBody = options.body !== undefined && options.body !== null && method !== 'GET'
      response = await fetch(url, {
        method,
        headers: {
          ...(hasBody ? { 'Content-Type': 'application/json;charset=UTF-8' } : {}),
          ...(options.isNeedRes !== undefined
            ? {
                isNeedRes:
                  typeof options.isNeedRes === 'boolean'
                    ? String(options.isNeedRes)
                    : options.isNeedRes,
              }
            : {}),
          ...(options.headers || {}),
        },
        body: hasBody ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      })
    } catch (e) {
      return fail(isAbortError(e) ? 'timeout' : 'unreachable', String(e))
    } finally {
      clearTimeout(timer)
    }

    if (!response.ok) {
      return fail('httpError', `本地服务返回 HTTP ${response.status}`)
    }

    // 3. 解析响应：优先 JSON，解析失败时保留原始文本
    const text = await response.text()
    let data: any = text
    if (text) {
      try {
        data = JSON.parse(text)
      } catch (e) {
        // 非 JSON 响应，data 保持为原始文本
      }
    }
    return finish({ ok: true, data })
  } catch (e) {
    return fail('unknown', String(e))
  }
}

function buildQuery(params?: Record<string, string | number | boolean | undefined | null>): string {
  if (!params) {
    return ''
  }
  const pairs = Object.keys(params)
    .filter((key) => params[key] !== undefined && params[key] !== null)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
  return pairs.length > 0 ? `?${pairs.join('&')}` : ''
}

function isAbortError(e: any): boolean {
  return !!e && typeof e === 'object' && e.name === 'AbortError'
}

/** 安全执行回调：回调内部抛出的异常同样被吞掉，保证 fire-and-forget 场景不产生未处理报错 */
function safeInvoke(fn: ((...args: any[]) => void) | undefined, ...args: any[]): void {
  if (!fn) {
    return
  }
  try {
    fn(...args)
  } catch (e) {
    // 吞掉回调内部异常，不影响主业务
  }
}
